import React, { useState, useEffect, useRef } from 'react'
import { Input, Button, Radio } from '@/components/atoms/index'
import { Switch, ConfigProvider } from 'antd'
import AcademyWordTable from '@/components/organisms/Table/AcademyWordTable'
import VocaSelectionContent from '@/components/Modal/VocaSelectionContent'
import { Dialog } from '@/components/organisms/Modal/Dialog'
import { Modal } from '@/components/organisms/Modal/Modal'
import { useNavigate, useParams } from 'react-router-dom'
import { academyWordExamApi, academyWordbookApi, academyWordApi } from '@/services/api'
import { useSessionSlug } from '@/hooks/useSessionSlug'

interface WordItem {
  id: string
  wordCode: string
  word: string
  meaning: string
  ttsType: string
  isPublic: boolean
  registrant: string
  createdAt: string
  uploadStatus: 'completed' | 'failed'
}

interface WordbookItem {
  id: string
  wordbookName: string
  wordCount: number
  isPublic: boolean
  registrant: string
  createdAt: string
}

interface WordInput {
  id: number
  word: string
  meaning: string
  wordId?: number
}

const createInitialWordInputs = (count = 10): WordInput[] =>
  Array.from({ length: count }, (_, idx) => ({
    id: idx + 1,
    word: '',
    meaning: ''
  }));

const ensureMinimumWordInputs = (inputs: WordInput[], min = 10): WordInput[] => {
  if (inputs.length >= min) return inputs;
  const maxId = inputs.reduce((max, item) => Math.max(max, item.id), 0);
  const extras = Array.from({ length: min - inputs.length }, (_, idx) => ({
    id: maxId + idx + 1,
    word: '',
    meaning: ''
  }));
  return [...inputs, ...extras];
};

export default function AcademyWordExamDetail() {
  const navigate = useNavigate()
  const { wordExamId } = useParams()
  const [loading, setLoading] = useState(false)
  const { academyCode: sessionAcademyCode } = useSessionSlug()
  const academyCode = sessionAcademyCode || ''
  
  // 기본 정보
  const [examName, setExamName] = useState('')
  const [examType, setExamType] = useState('word') // word | meaning
  const [uploadType, setUploadType] = useState('wordbook') // wordbook | individual
  const [isPublic, setIsPublic] = useState(true)
  
  // 단어장 선택 방식
  const [wordbookModalOpen, setWordbookModalOpen] = useState(false)
  const [selectedWordbooks, setSelectedWordbooks] = useState<WordbookItem[]>([])
  const [availableWordbooks, setAvailableWordbooks] = useState<WordbookItem[]>([])
  const [selectedWordbookWords, setSelectedWordbookWords] = useState<WordItem[]>([])
  const [showWordbookWordList, setShowWordbookWordList] = useState(true)
  
  // 개별 등록 방식
  const [wordInputs, setWordInputs] = useState<WordInput[]>(createInitialWordInputs())
  const [wordSuggestions, setWordSuggestions] = useState<any[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)
  const [activeInputId, setActiveInputId] = useState<number | null>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (uploadType === 'individual') {
      setWordInputs(prev => ensureMinimumWordInputs(prev))
    }
  }, [uploadType])

  // 단어 자동완성 검색
  const searchWords = async (query: string) => {
    if (!query || query.length < 1) {
      setWordSuggestions([])
      return
    }
    try {
      if (!academyCode) return
      const res = await academyWordApi.list({ q: query, academyCode, page: 0, size: 20 })
      const data = res?.data || res
      const content = data?.content || []
      setWordSuggestions(content)
    } catch (e) {
      console.error(e)
      setWordSuggestions([])
    }
  }

  // 단어장 리스트 로드
  const loadWordbooks = async () => {
    try {
      if (!academyCode) return
      const res = await academyWordbookApi.list({ q: '', academyCode, page: 0, size: 100 })
      const data = res?.data || res
      const content = data?.content || []
      const mapped = content.map((item: any) => ({
        id: String(item.id),
        wordbookName: item.title,
        wordCount: item.itemCount || 0,
        isPublic: !!item.isPublic,
        registrant: item.registrant || '',
        createdAt: (item.createdAt || '').toString().substring(0, 10)
      }))
      setAvailableWordbooks(mapped)
    } catch (e) {
      console.error(e)
    }
  }

  // 선택된 단어장의 단어들 로드
  const loadWordsFromWordbooks = async () => {
    if (selectedWordbooks.length === 0) {
      setSelectedWordbookWords([])
      return
    }
    try {
      const allWords: WordItem[] = []
      for (const wb of selectedWordbooks) {
        const res = await academyWordbookApi.detail(wb.id)
        const data = res?.data || res
        const words = data?.words || []
        words.forEach((w: any) => {
          allWords.push({
            id: String(w.id),
            wordCode: w.code || '',
            word: w.word || '',
            meaning: w.meaning || '',
            ttsType: w.ttsType || '',
            isPublic: true,
            registrant: '',
            createdAt: '',
            uploadStatus: 'completed'
          })
        })
      }
      setSelectedWordbookWords(allWords)
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    loadWordsFromWordbooks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWordbooks])

  // 단어장 선택 모달 열기
  const handleOpenWordbookModal = () => {
    loadWordbooks()
    setWordbookModalOpen(true)
  }

  // 단어장 선택 완료
  const handleWordbookSelectionOk = (selected: WordbookItem[]) => {
    setSelectedWordbooks(selected)
    setWordbookModalOpen(false)
  }

  // 단어 입력 추가
  const handleAddWord = () => {
    const newId = Math.max(...wordInputs.map(input => input.id), 0) + 1
    setWordInputs(prev => [...prev, { id: newId, word: '', meaning: '' }])
  }

  // 단어 입력 삭제
  const handleDeleteWord = (id: number) => {
    if (wordInputs.length === 1) return
    setWordInputs(prev => prev.filter(input => input.id !== id))
  }

  // 단어 입력 변경 (자동완성 포함)
  const handleWordInputChange = (id: number, field: 'word' | 'meaning', value: string) => {
    setWordInputs(prev => 
      prev.map(input => 
        input.id === id ? { ...input, [field]: value } : input
      )
    )
    
    // 단어 필드에서 입력 시 자동완성 검색
    if (field === 'word') {
      setActiveInputId(id)
      searchWords(value)
      setShowSuggestions(true)
      setActiveSuggestionIndex(-1)
    }
  }

  // 자동완성 항목 선택
  const handleSelectSuggestion = (id: number, wordData: any) => {
    setWordInputs(prev => 
      prev.map(input => 
        input.id === id ? { 
          ...input, 
          word: wordData.word, 
          meaning: wordData.meaning,
          wordId: wordData.id 
        } : input
      )
    )
    setShowSuggestions(false)
    setWordSuggestions([])
    setActiveInputId(null)
  }

  // 단어장에서 단어 삭제
  const handleDeleteWordbookWord = (wordId: string) => {
    setSelectedWordbookWords(prev => prev.filter(w => w.id !== wordId))
  }

  // PDF 다운로드 핸들러
  const handleDownloadExam = async () => {
    if (!wordExamId) return
    try {
      const blob = await academyWordExamApi.downloadExam(Number(wordExamId))
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${examName}_시험지.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      const msg = e?.response?.data?.message || '다운로드 중 오류가 발생했습니다.'
      await Modal.alert({ message: msg })
    }
  }

  const handleDownloadAnswer = async () => {
    if (!wordExamId) return
    try {
      const blob = await academyWordExamApi.downloadAnswer(Number(wordExamId))
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${examName}_답지.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      const msg = e?.response?.data?.message || '다운로드 중 오류가 발생했습니다.'
      await Modal.alert({ message: msg })
    }
  }

  // 등록/수정
  const handleSubmit = async () => {
    if (!examName.trim()) {
      await Modal.alert({ message: '단어 시험명을 입력해주세요.' })
      return
    }

    const registrantName = localStorage.getItem('academy_user_name') || ''
    if (!academyCode) {
      await Modal.alert({ message: '학원 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.' })
      return
    }
    
    let payload: any = {
      name: examName,
      examType: examType,
      wordSubmitMethod: uploadType,
      isPublic: isPublic,
      registrant: registrantName,
      academyCode: academyCode || undefined
    }

    if (uploadType === 'wordbook') {
      // 단어장 방식
      if (selectedWordbooks.length === 0) {
        await Modal.alert({ message: '최소 1개 이상의 단어장을 선택해주세요.' })
        return
      }
      const wordbookIds = selectedWordbooks.map(w => w.id)
      payload.wordbookIds = JSON.stringify(wordbookIds)
    } else {
      // 개별 등록 방식
      const validWords = wordInputs.filter(w => w.word.trim() && w.meaning.trim())
      if (validWords.length === 0) {
        await Modal.alert({ message: '최소 1개 이상의 단어를 입력해주세요.' })
        return
      }
      payload.words = JSON.stringify(validWords.map(w => ({
        word: w.word.trim(),
        meaning: w.meaning.trim(),
        wordId: w.wordId
      })))
    }

    setLoading(true)
    try {
      if (wordExamId) {
        await academyWordExamApi.update(Number(wordExamId), payload)
        await Modal.alert({ message: '단어시험이 수정되었습니다.' })
      } else {
        await academyWordExamApi.create(payload)
        await Modal.alert({ message: '단어시험이 등록되었습니다.' })
      }
      navigate('/academy/word/word_exam/list')
    } catch (e: any) {
      const msg = e?.response?.data?.message || '처리 중 오류가 발생했습니다.'
      await Modal.alert({ message: msg })
    } finally {
      setLoading(false)
    }
  }

  // 상세 조회 (수정 시)
  useEffect(() => {
    if (!wordExamId) return
    const fetchDetail = async () => {
      try {
        const res = await academyWordExamApi.detail(Number(wordExamId))
        const data = res?.data || res
        setExamName(data.name || '')
        setExamType(data.examType || 'word')
        setUploadType(data.wordSubmitMethod || 'wordbook')
        setIsPublic(!!data.isPublic)
        
        if (data.wordSubmitMethod === 'individual' && data.words) {
          try {
            const words = JSON.parse(data.words)
            const mapped = words.map((w: any, idx: number) => ({
              id: idx + 1,
              word: w.word || '',
              meaning: w.meaning || '',
              wordId: w.wordId
            }))
            setWordInputs(ensureMinimumWordInputs(mapped))
          } catch (e) {
            console.error(e)
          }
        }
        
        if (data.wordSubmitMethod === 'wordbook' && data.wordbookIds) {
          try {
            const wbIds = JSON.parse(data.wordbookIds)
            // 단어장 ID 배열로 단어장 정보 로드
            const wordbookPromises = wbIds.map((id: string) => academyWordbookApi.detail(id))
            const wordbookResults = await Promise.all(wordbookPromises)
            const loadedWordbooks = wordbookResults.map((res: any) => {
              const wbData = res?.data || res
              return {
                id: String(wbData.id),
                wordbookName: wbData.title || '',
                wordCount: wbData.itemCount || 0,
                isPublic: !!wbData.isPublic,
                registrant: wbData.registrant || '',
                createdAt: (wbData.createdAt || '').toString().substring(0, 10)
              }
            })
            setSelectedWordbooks(loadedWordbooks)
          } catch (e) {
            console.error(e)
          }
        }
      } catch (e) {
        console.error(e)
      }
    }
    fetchDetail()
  }, [wordExamId])

  return (
    <main className='flex flex-col flex-1'>
      <div className='flex flex-row justify-between items-center pt-8'>
        <h1 className='pb-5 heading1'>{wordExamId ? '단어시험 수정' : '단어시험 등록'}</h1>
        
        {wordExamId && (
          <div className='flex flex-row gap-2'>
            <Button intent='primary' className='' onClick={handleDownloadExam}>시험지 PDF 다운로드</Button>
            <Button intent='primary' outline className='' onClick={handleDownloadAnswer}>답지 PDF 다운로드</Button>
          </div>
        )}
      </div>

      <div className="w-full">
        <ul>    
          <li className='flex py-5 border-b border-gray-200'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>단어 시험명</p>
            <Input
              placeholder='단어 시험명을 입력해주세요.'
              className='w-full max-w-[500px]'
              value={examName}
              onChange={(e) => setExamName(e.target.value)}
            />
          </li>       

          <li className='flex py-5 border-b border-gray-200'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>시험 유형</p>
            <div className='flex flex-row gap-5'>
              <Radio 
                name='word-exam-type' 
                checked={examType === 'word'} 
                label='단어 작성형' 
                onChange={() => setExamType('word')} 
              />
              <Radio 
                name='word-exam-type' 
                checked={examType === 'meaning'} 
                label='해석 작성형' 
                onChange={() => setExamType('meaning')} 
              />
            </div>
          </li>

          <li className='flex py-5 border-b border-gray-200'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>단어 출제 방법</p>
            <div className='flex flex-row gap-5'>
              <Radio 
                name='word-exam-upload-type' 
                checked={uploadType === 'wordbook'} 
                label='단어집에서 가져오기' 
                onChange={() => setUploadType('wordbook')} 
              />
              <Radio 
                name='word-exam-upload-type' 
                checked={uploadType === 'individual'} 
                label='단어 개별 등록하기' 
                onChange={() => setUploadType('individual')} 
              />
            </div>
          </li>

          <li className='flex py-5 border-b border-gray-200'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>출제 단어</p>
            
            {/* 단어집 가져오기 섹션 */}
            {uploadType === 'wordbook' && (
              <section className='w-full'>
                <div className='flex flex-row gap-2 w-full max-w-[500px] mb-4'>
                  <Button className='w-[150px]' onClick={handleOpenWordbookModal}>
                    단어장 선택
                  </Button>
                  <span className='flex items-center text-sm text-gray-500'>
                    {selectedWordbooks.length}개 선택됨
                  </span>
                </div>

                {selectedWordbooks.length > 0 && (
                  <div className='mb-4'>
                    <p className='mb-2 text-sm font-medium'>선택된 단어장:</p>
                    <div className='flex flex-wrap gap-2'>
                      {selectedWordbooks.map(wb => (
                        <span key={wb.id} className='px-3 py-1 text-sm text-blue-700 bg-blue-100 rounded-full'>
                          {wb.wordbookName} ({wb.wordCount}개)
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className=''>
                  <div className='flex gap-3 justify-between items-center py-2'>
                    <p className='text-sm text-gray-500'>
                      등록된 단어 수 : {selectedWordbookWords.length}개
                    </p>
                    <button
                      type='button'
                      className='text-sm text-indigo-600 hover:underline'
                      onClick={() => setShowWordbookWordList(prev => !prev)}
                    >
                      {showWordbookWordList ? '단어 목록 접기' : '단어 목록 펼치기'}
                    </button>
                  </div>
                  {showWordbookWordList && (
                    <div className='max-h-[300px] overflow-y-auto'>
                      <AcademyWordTable  
                        data={selectedWordbookWords}
                        type='wordBookUpload'
                        onRowClick={() => {}}
                        onDelete={handleDeleteWordbookWord}
                      />
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* 단어 개별 등록하기 섹션 */}
            {uploadType === 'individual' && (
              <section className='w-full max-w-[800px]'>
                <div className='flex flex-col gap-2'>
                  {wordInputs.map((input, index) => (
                    <div key={input.id} className='flex relative flex-col gap-1'>
                      {index === 0 && (
                        <div className='flex flex-row gap-2 *:w-full'>
                          <p className='text-gray-500 max-w-[350px]'>단어</p>
                          <p className='text-gray-500 max-w-[350px]'>해석</p>
                        </div>
                      )}
                      <div className='flex flex-row gap-2 items-center'>
                        <div className='relative max-w-[350px] w-full'>
                          <Input 
                            placeholder='단어를 입력하세요 (자동완성)'
                            className='w-full'
                            value={input.word}
                            onChange={(e) => handleWordInputChange(input.id, 'word', e.target.value)}
                            onFocus={() => {
                              setActiveInputId(input.id)
                              if (input.word) searchWords(input.word)
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowSuggestions(false)
                                setActiveInputId(null)
                              }, 200)
                            }}
                          />
                          {/* 자동완성 드롭다운 */}
                          {showSuggestions && activeInputId === input.id && wordSuggestions.length > 0 && (
                            <div 
                              ref={suggestionsRef}
                              className='absolute z-50 w-full min-w-[400px] mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto'
                            >
                              {wordSuggestions.map((suggestion, idx) => (
                                <div
                                  key={suggestion.id}
                                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                                    idx === activeSuggestionIndex ? 'bg-gray-100' : ''
                                  }`}
                                  onClick={() => handleSelectSuggestion(input.id, suggestion)}
                                >
                                  <div className='flex gap-2 justify-between items-start'>
                                    <div className='flex-1 min-w-0'>
                                      <div className='font-medium'>{suggestion.word}</div>
                                      <div className='text-sm text-gray-600'>{suggestion.meaning}</div>
                                    </div>
                                    {(suggestion.gender || suggestion.age || suggestion.speed || suggestion.pitch) && (
                                      <div className='flex flex-col gap-0.5 text-xs flex-shrink-0'>
                                        {(suggestion.gender || suggestion.age) && (
                                          <span className='text-gray-500 whitespace-nowrap'>
                                            {suggestion.gender === 'male' ? '남성' : suggestion.gender === 'female' ? '여성' : suggestion.gender || ''}
                                            {suggestion.gender && suggestion.age && ' / '}
                                            {suggestion.age || ''}
                                          </span>
                                        )}
                                        {(suggestion.speed || suggestion.pitch) && (
                                          <span className='text-gray-500 whitespace-nowrap'>
                                            {suggestion.speed && `속도: ${suggestion.speed}`}
                                            {suggestion.speed && suggestion.pitch && ' / '}
                                            {suggestion.pitch && `피치: ${suggestion.pitch}`}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <Input 
                          placeholder='해석을 입력해주세요.'
                          className='w-full max-w-[350px]'
                          value={input.meaning}
                          onChange={(e) => handleWordInputChange(input.id, 'meaning', e.target.value)}
                        />
                        {index > 0 && (
                          <Button 
                            intent='danger' 
                            outline 
                            className='w-[150px]'
                            onClick={() => handleDeleteWord(input.id)}
                          >
                            삭제
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Button className='w-[150px] mt-4' onClick={handleAddWord}>단어 추가</Button>
              </section>
            )}
          </li>        
          
          <li className='flex py-5 border-b border-gray-200'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>공개 설정</p>
            <div className='flex flex-col gap-2 w-full max-w-[500px]'>
              <div className='flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-4'>
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: '#6366f1', // indigo-500
                    },
                  }}
                >
                  <Switch
                    checked={isPublic}
                    onChange={setIsPublic}
                    checkedChildren="공개"
                    unCheckedChildren="비공개"
                    aria-label='word-exam-visibility-toggle'
                  />
                </ConfigProvider>
                <span className='text-sm text-gray-600'>
                  {isPublic ? '모든 학원/학생이 열람 가능' : '해당 학원 내에서만 사용'}
                </span>
              </div>
              <p className='text-xs text-gray-500'>
                공개 전환 시 다른 학원에서도 이 시험지를 선택할 수 있으며, 비공개로 두면 우리 학원 수업에만 노출됩니다.
              </p>
            </div>
          </li>

          <li className='flex py-5 border-b border-gray-200'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>등록자</p>
            <div className='flex flex-row gap-2 w-full max-w-[500px]'>
              <p>{localStorage.getItem('academy_user_name') || '-'}</p>
            </div>
          </li>
        </ul>

        <section className='flex flex-row gap-2 justify-end my-5 w-full'>
          <Button intent='secondary' className='w-[150px]' onClick={() => navigate(-1)}>
            뒤로가기
          </Button>
          <Button 
            intent='primary' 
            className='w-[150px]' 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? '처리중...' : (wordExamId ? '수정하기' : '등록하기')}
          </Button>
        </section>
      </div>
      
      {/* 단어장 선택 모달 - VocaSelectionContent 사용 */}
      <Dialog
        open={wordbookModalOpen}
        onCancel={() => setWordbookModalOpen(false)}
        onOk={() => setWordbookModalOpen(false)}
        className='!max-w-[800px]'
        hideFooter={true}
      >
        <VocaSelectionContent
          availableWords={availableWordbooks}
          initialSelectedWords={selectedWordbooks}
          onOk={handleWordbookSelectionOk}
          onCancel={() => setWordbookModalOpen(false)}
          title="단어장 선택"
        />
      </Dialog>
    </main>
  )
}
